/**
 * Payment Hook using TanStack Query (V2)
 * Matches the old usePayment.ts implementation exactly for easy migration
 */
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useBasisTheory } from '@basis-theory/basis-theory-react';
import { getBasisTheoryApiKey } from '../../../react/config/payment';
import { useTagadaContext } from '../providers/TagadaProvider';
import { PaymentsResource } from '../../core/resources/payments';
import { TagadaError, TagadaErrorCode } from '../../core/errors';
import { usePaymentPolling } from './usePaymentPolling';
import { useThreeds } from './useThreeds';
import { getGlobalApiClient } from './useApiQuery';
import { useStoreConfigQuery } from './useStoreConfigQuery';
import { usePaymentActionHandler } from './payment-actions/usePaymentActionHandler';
import { useAirwallex3dsReturn } from './payment-redirect/useAirwallex3dsReturn';
import { useGenericPaymentReturn } from './payment-redirect/useGenericPaymentReturn';
import { usePaymentInstruments } from './payment-processing/usePaymentInstruments';
import { usePaymentProcessors } from './payment-processing/usePaymentProcessors';
export function usePaymentQuery(hookOptions) {
    const { environment } = useTagadaContext();
    // Get store config to auto-detect 3DS setting from payment flow
    const { storeConfig } = useStoreConfigQuery();
    // Create payments resource client
    const paymentsResource = useMemo(() => {
        try {
            return new PaymentsResource(getGlobalApiClient());
        }
        catch (error) {
            throw new Error('Failed to initialize payments resource: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPaymentId, setCurrentPaymentId] = useState(null);
    // Store hook-level callbacks in ref to avoid re-renders
    const hookOptionsRef = useRef(hookOptions);
    useEffect(() => {
        hookOptionsRef.current = hookOptions;
    }, [hookOptions]);
    const { startPolling, stopPolling } = usePaymentPolling();
    const { createSession, startChallenge } = useThreeds();
    // Track if we've already processed a redirect return (prevents double-processing)
    const redirectReturnProcessedRef = useRef(false);
    // Get API key from embedded configuration with proper environment detection
    const apiKey = useMemo(() => getBasisTheoryApiKey(), []); // Auto-detects environment
    // Initialize BasisTheory using React wrapper
    const { bt: basisTheory, error: btError } = useBasisTheory(apiKey, {
        elements: false,
    });
    // Handle BasisTheory initialization errors (only log once when state changes)
    useEffect(() => {
        if (btError) {
            setError('Failed to initialize payment processor: ' + btError.message);
        }
        else if (basisTheory && !error) {
            setError(null); // Clear any previous errors
        }
    }, [basisTheory, btError]); // Removed error from dependency to prevent loops
    // Clean up polling when component unmounts
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);
    // Use payment action handler hook
    const { handlePaymentAction } = usePaymentActionHandler({
        paymentsResource,
        startChallenge,
        startPolling,
        setIsLoading,
        setError,
        hookOptionsRef,
    });
    // Handle Airwallex 3DS redirect returns
    useAirwallex3dsReturn({
        paymentsResource,
        startPolling,
        handlePaymentAction,
        setIsLoading,
        setError,
        setCurrentPaymentId,
        hookOptionsRef,
        redirectReturnProcessedRef,
    });
    // Handle generic payment redirect returns
    useGenericPaymentReturn({
        startPolling,
        handlePaymentAction,
        setIsLoading,
        setError,
        setCurrentPaymentId,
        hookOptionsRef,
        redirectReturnProcessedRef,
    });
    // Use payment instruments hook
    const paymentInstruments = usePaymentInstruments({
        basisTheory,
        paymentsResource,
        setError,
    });
    const { createCardPaymentInstrument, createApplePayPaymentInstrument, createGooglePayPaymentInstrument, getCardPaymentInstruments, } = paymentInstruments;
    // Use payment processors hook
    const paymentProcessors = usePaymentProcessors({
        paymentsResource,
        createCardPaymentInstrument,
        createApplePayPaymentInstrument,
        createGooglePayPaymentInstrument,
        createSession,
        handlePaymentAction,
        startPolling,
        setIsLoading,
        setError,
        setCurrentPaymentId,
        hookOptionsRef,
        storeConfig,
    });
    const { processCardPayment, processApplePayPayment, processGooglePayPayment, processPaymentWithInstrument, processApmPayment, } = paymentProcessors;
    const clearError = useCallback(() => {
        setError(null);
    }, []);
    const tagadaError = useMemo(() => error ? new TagadaError(error, { code: TagadaErrorCode.PAYMENT_FAILED }) : null, [error]);
    return {
        processCardPayment,
        processApplePayPayment,
        processGooglePayPayment,
        processPaymentWithInstrument,
        processApmPayment,
        createCardPaymentInstrument,
        createApplePayPaymentInstrument,
        createGooglePayPaymentInstrument,
        getCardPaymentInstruments,
        isLoading: isLoading || !basisTheory,
        error: tagadaError,
        clearError,
        currentPaymentId,
    };
}
